import React from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import {
  DevData, DevEmployee, DVO_BOTS, DVO_STAFF, FeedList, StatStrip,
  isBusyStatus, roleColor, roleShort, statusMeta, teamOf, workLine, dvoTime, clip,
} from './devteam-shared';

/* ============================================================
   呈现设计 1-5：全息展厅 / 作战指挥室 / 流程轨道图 / 项目卷宗 / 数字驾驶舱
   每种设计=完全不同的布局、排版、交互密度与角色具象化图形。
   全部只读，数据来自共享总线 D。
   ============================================================ */

/* ---------------- ① 全息展厅：机器人站台 + 数据环 ---------------- */
export const HoloHallDesign: React.FC<{ D: DevData }> = ({ D }) => {
  const team = teamOf(D);
  const running = (D.summary?.activeTasks || 0) + (D.summary?.runningDelegates || 0);
  return (
    <div className="dsn-holo">
      <div className="floor" />
      {/* 中央项目核心环 */}
      <div className={`core ${running ? 'run' : ''}`}>
        <i className="ring r1" /><i className="ring r2" /><i className="ring r3" />
        <div className="in">
          <b>{D.project.name}</b>
          <em>{running ? `${running} 项工作推进中` : '待命'}</em>
        </div>
      </div>
      {/* 机器人团队站台 */}
      <div className="staff">
        {!D.loaded && <div className="dvo-empty"><LoadingOutlined /> 正在进入办公室…</div>}
        {D.loaded && team.length === 0 && <div className="dvo-empty">团队待组建，交办需求后自动进场</div>}
        {team.map((e, i) => {
          const busy = isBusyStatus(e.status);
          const st = statusMeta(e.status);
          return (
            <div key={e.id} className={`bot ${busy ? 'busy' : ''} tn-${st.tone}`} style={{ '--i': String(i), '--rc': roleColor(e.role) } as React.CSSProperties}>
              <span className="pad"><i /></span>
              <img src={DVO_BOTS[i % DVO_BOTS.length]} alt="" draggable={false} />
              <span className="chip">{st.label}</span>
              <div className="tag">
                <b>{e.name}</b>
                <em>{e.role}</em>
                <u>{workLine(D, e)}</u>
              </div>
            </div>
          );
        })}
      </div>
      {/* 左：态势 · 右：实时沟通 */}
      <div className="lstats"><StatStrip D={D} /></div>
      <aside className="rfeed">
        <div className="cap">团队沟通实录</div>
        <FeedList D={D} limit={18} />
      </aside>
    </div>
  );
};

/* ---------------- ② 作战指挥室：六边形军牌 + 战情横幅 ---------------- */
export const WarRoomDesign: React.FC<{ D: DevData }> = ({ D }) => {
  const team = teamOf(D);
  return (
    <div className="dsn-war">
      <div className="banner">
        <i className="lamp" />
        <b>{D.summary?.displayText || '战情室静默，等待指令'}</b>
        <StatStrip D={D} />
      </div>
      <div className="formation">
        {team.length === 0 && <div className="dvo-empty">尚无参战人员</div>}
        {team.map((e, i) => {
          const busy = isBusyStatus(e.status);
          const st = statusMeta(e.status);
          return (
            <div key={e.id} className={`hexcard ${busy ? 'busy' : ''} tn-${st.tone}`} style={{ '--i': String(i), '--rc': roleColor(e.role) } as React.CSSProperties}>
              <div className="hex">
                <span className="rs">{roleShort(e.role)}</span>
                <i className="scan" />
              </div>
              <b>{e.name}</b>
              <em>{e.role}</em>
              <u className="op">{workLine(D, e)}</u>
              <span className="st">{st.label}</span>
            </div>
          );
        })}
      </div>
      <div className="intel">
        <div className="cap">◈ 前线情报电传</div>
        <FeedList D={D} limit={14} />
      </div>
    </div>
  );
};

/* ---------------- ③ 流程轨道图：流程当主角 ---------------- */
export const MetroDesign: React.FC<{ D: DevData; flow: React.ReactNode }> = ({ D, flow }) => {
  const team = teamOf(D);
  return (
    <div className="dsn-metro">
      <div className="terminals">
        {team.map((e) => {
          const busy = isBusyStatus(e.status);
          return (
            <div key={e.id} className={`term ${busy ? 'busy' : ''}`} style={{ '--rc': roleColor(e.role) } as React.CSSProperties}>
              <i className="light" />
              <b>{e.name}</b>
              <em>{roleShort(e.role)} 线</em>
              <u>{statusMeta(e.status).label}</u>
            </div>
          );
        })}
        <div className="tstats"><StatStrip D={D} /></div>
      </div>
      <div className="railboard">{flow}</div>
      <aside className="sidefeed">
        <div className="cap">线路广播</div>
        <FeedList D={D} limit={12} />
      </aside>
    </div>
  );
};

/* ---------------- ④ 项目卷宗：牛皮纸 + 写实工牌 + 红章 ---------------- */
export const DossierDesign: React.FC<{ D: DevData }> = ({ D }) => {
  const team = teamOf(D);
  const running = (D.summary?.activeTasks || 0) + (D.summary?.runningDelegates || 0);
  const seal = D.pending ? '待批示' : running ? '研发中' : '备案';
  return (
    <div className="dsn-dossier">
      <div className="folder">
        <div className="fh">
          <em>技术研发公司 · 项目卷宗</em>
          <b>{D.project.name}</b>
          <span className={`seal ${D.pending ? 'warn' : running ? 'run' : ''}`}>{seal}</span>
        </div>
        <div className="fstats"><StatStrip D={D} /></div>
        <div className="badges">
          {team.length === 0 && <div className="dvo-empty">卷宗内暂无在册人员</div>}
          {team.map((e, i) => {
            const busy = isBusyStatus(e.status);
            return (
              <div key={e.id} className={`idcard ${busy ? 'busy' : ''}`} style={{ '--i': String(i), '--rc': roleColor(e.role) } as React.CSSProperties}>
                <div className="photo">
                  <img src={DVO_STAFF[i % DVO_STAFF.length]} alt="" draggable={false} />
                  {busy && <span className="stamp">办理中</span>}
                </div>
                <b>{e.name}</b>
                <em>{e.role}</em>
                <u>{workLine(D, e)}</u>
              </div>
            );
          })}
        </div>
      </div>
      <aside className="paper">
        <div className="cap">往来记事</div>
        <FeedList D={D} limit={16} />
      </aside>
    </div>
  );
};

/* ---------------- ⑤ 数字驾驶舱：仪表盘 + 团队雷达 ---------------- */
const Gauge: React.FC<{ label: string; value: number; max: number; unit?: string; tone?: string }> = ({ label, value, max, unit, tone }) => {
  const pct = max > 0 ? Math.min(1, value / max) : 0;
  return (
    <div className={`gauge ${tone || ''}`} style={{ '--p': String(pct * 270) } as React.CSSProperties}>
      <i className="dial" />
      <div className="rd"><b>{value}</b>{unit && <em>{unit}</em>}</div>
      <span>{label}</span>
    </div>
  );
};

export const CockpitDesign: React.FC<{ D: DevData }> = ({ D }) => {
  const team = teamOf(D);
  const busyN = team.filter((e) => isBusyStatus(e.status)).length;
  return (
    <div className="dsn-cockpit">
      <div className="gauges">
        <Gauge label="团队负荷" value={busyN} max={Math.max(1, team.length)} unit={`/${team.length}人`} />
        <Gauge label="进行中任务" value={D.summary?.activeTasks || 0} max={Math.max(4, (D.summary?.activeTasks || 0))} />
        <Gauge label="执行中委派" value={D.summary?.runningDelegates || 0} max={Math.max(4, (D.summary?.runningDelegates || 0))} />
        <div className="warns">
          <span className={`wl ${D.summary?.blockedTasks ? 'on bad' : ''}`}><i />受阻 {D.summary?.blockedTasks || 0}</span>
          <span className={`wl ${D.pending ? 'on warn' : ''}`}><i />待确认 {D.pending}</span>
        </div>
      </div>
      <div className="radar">
        <i className="grid" /><i className="sweep" />
        <div className="cn">{D.project.name}</div>
        {team.map((e, i) => {
          const busy = isBusyStatus(e.status);
          const ang = (i / Math.max(1, team.length)) * 360 - 90;
          const r = busy ? 27 : 40;
          return (
            <div
              key={e.id}
              className={`blip ${busy ? 'busy' : ''}`}
              style={{
                '--rc': roleColor(e.role),
                left: `${50 + r * Math.cos((ang * Math.PI) / 180)}%`,
                top: `${50 + r * Math.sin((ang * Math.PI) / 180)}%`,
              } as React.CSSProperties}
              title={workLine(D, e)}
            >
              <i />
              <span>{e.name}<em>{statusMeta(e.status).label}</em></span>
            </div>
          );
        })}
      </div>
      <div className="console">
        <div className="cap">通讯记录仪</div>
        <FeedList D={D} limit={10} />
      </div>
    </div>
  );
};
